import { useState } from 'react';
import { BarcodeType1, BarcodeType1Create } from '../types/costwand.d';
import { useBarcodeType1 } from '../hooks/useBarcodeType1.ts';
import BarcodeType1Form from '../components/BarcodeType1Form.tsx';
import BarcodeType1Table from '../components/BarcodeType1Table.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const BarcodeType1Page = () => {
  const {
    BarcodeType1,
    loading,
    error, // This error is for the overall fetch, not form submission
    currentPage,
    totalPages,
    totalItems, // Destructure totalItems from the hook
    handlePageChange,
    createBarcodeType1,
    updateBarcodeType1,
    deleteBarcodeType1,
    fetchBarcodeType1, // Assuming useBarcodeType1 provides a way to refetch
  } = useBarcodeType1();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingBarcodeType1, setEditingBarcodeType1] = useState<BarcodeType1 | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddBarcodeType1 = () => {
    setEditingBarcodeType1(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditBarcodeType1 = (barcodeType1: BarcodeType1) => {
    setEditingBarcodeType1(barcodeType1);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteBarcodeType1 = async (id: number) => {
    // The useBarcodeType1 hook should handle its own loading/error for delete
    await deleteBarcodeType1(id);
    // Refetch Barcode Type 1s after deletion to ensure the list is up-to-date
    fetchBarcodeType1(currentPage);
  };

  const handleFormSubmit = async (data: BarcodeType1Create) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingBarcodeType1) {
        success = await updateBarcodeType1(editingBarcodeType1.id, data);
      } else {
        success = await createBarcodeType1(data);
      }

      if (success) {
        setIsFormOpen(false); // Close form dialog on success
        fetchBarcodeType1(currentPage); // Re-fetch to update the list
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // This catch block is for unexpected errors not handled by the hook's return value
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingBarcodeType1(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Barcode Type 1 Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddBarcodeType1}>Add New Barcode Type 1</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingBarcodeType1 ? 'Edit Barcode Type 1' : 'Add New Barcode Type 1'}</DialogTitle>
              <DialogDescription>
                {editingBarcodeType1 ? 'Make changes to the barcode type 1 here.' : 'Add a new barcode type 1 to your list.'}
              </DialogDescription>
            </DialogHeader>
            <BarcodeType1Form
              initialData={editingBarcodeType1}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <BarcodeType1Table
        BarcodeType1={BarcodeType1}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems} // Pass totalItems to the table
        onPageChange={handlePageChange}
        onEdit={handleEditBarcodeType1}
        onDelete={handleDeleteBarcodeType1}
        loading={loading}
        error={error} // This error is for the table's data fetching
        baseEndpoint="/barcode_1" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default BarcodeType1Page;